import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw, Undo2, Redo2, Trash2 } from 'lucide-react';
import { useDesignStore } from '../../store/designStore';
import { useHistoryStore } from '../../store/historyStore';
import { useConfirm } from '../Notifications';
import type { FabricCanvas } from '../../types/fabric';
import { getLogicalSize } from '../../lib/canvasObjectHelpers';

interface TopToolbarProps {
  canvas: FabricCanvas | null;
}

export function TopToolbar({ canvas }: TopToolbarProps) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { selectedObject } = useDesignStore();
  const { undo, redo } = useHistoryStore();
  const [, tick] = useState(0);

  useEffect(() => {
    if (!canvas) return;
    const refresh = () => tick(n => n + 1);
    canvas.on('selection:created', refresh);
    canvas.on('selection:updated', refresh);
    canvas.on('selection:cleared', refresh);
    canvas.on('object:modified', refresh);
    return () => {
      canvas.off('selection:created', refresh);
      canvas.off('selection:updated', refresh);
      canvas.off('selection:cleared', refresh);
      canvas.off('object:modified', refresh);
    };
  }, [canvas]);

  const hasSelection = !!selectedObject;
  const obj = selectedObject as any;

  const alignObject = (direction: 'left' | 'center' | 'right' | 'top' | 'bottom') => {
    if (!canvas || !obj) return;
    // Use logical (unzoomed) canvas dimensions — canvas.width/height are zoomed
    // pixel dimensions that would misplace objects at any zoom level other than 1.
    const { width: logW, height: logH } = getLogicalSize(canvas);
    const ow = (obj.width  ?? 0) * (obj.scaleX ?? 1);
    const oh = (obj.height ?? 0) * (obj.scaleY ?? 1);
    // Objects may use originX/originY 'center' (added via centerObjectLogical) or
    // 'left'/'top' (Fabric defaults). Convert to a uniform "left-edge" position,
    // apply the alignment, then convert back to the object's own origin convention.
    const originX = obj.originX ?? 'left';
    const originY = obj.originY ?? 'top';
    // Anchor offsets: how far `obj.left`/`obj.top` is from the object's own left/top edge
    const anchorLeft  = originX === 'center' ? ow / 2 : originX === 'right' ? ow : 0;
    const anchorTop   = originY === 'center' ? oh / 2 : originY === 'bottom' ? oh : 0;
    if (direction === 'left')   obj.set({ left: anchorLeft });
    if (direction === 'right')  obj.set({ left: logW - ow + anchorLeft });
    if (direction === 'center') obj.set({ left: (logW - ow) / 2 + anchorLeft, top: (logH - oh) / 2 + anchorTop });
    if (direction === 'top')    obj.set({ top: anchorTop });
    if (direction === 'bottom') obj.set({ top: logH - oh + anchorTop });
    obj.setCoords();
    canvas.renderAll();
  };

  const resetCanvas = async () => {
    if (!canvas) return;
    const ok = await confirm(t('toolbar.resetConfirm'), { confirmLabel: t('toolbar.resetCanvas'), danger: true });
    if (!ok) return;
    canvas.clear();
    canvas.renderAll();
  };

  type AlignDir = Parameters<typeof alignObject>[0];
  const alignButtons: { dir: AlignDir; icon: React.ReactNode; title: string }[] = [
    { dir: 'left',   icon: alignIconLeft,   title: t('toolbar.alignLeft') },
    { dir: 'center', icon: alignIconCenter, title: t('toolbar.alignCenter') },
    { dir: 'right',  icon: alignIconRight,  title: t('toolbar.alignRight') },
    { dir: 'top',    icon: alignIconTop,    title: t('toolbar.alignTop') },
    { dir: 'bottom', icon: alignIconBottom, title: t('toolbar.alignBottom') },
  ];

  return (
    <div style={{
      height: 60,
      background: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      padding: '0 14px',
      gap: 8,
      fontSize: 14,
      flexShrink: 0,
    }}>
      {/* History reset */}
      <button className="toolbar-btn" onClick={resetCanvas} style={{ ...btn, color: '#ef4444' }} title={t('toolbar.resetCanvas')}>
        <RotateCcw size={19} strokeWidth={1.75} />
      </button>
      <button className="toolbar-btn" onClick={() => canvas && undo(canvas)} style={btn} title={t('designer.undo')}>
        <Undo2 size={19} strokeWidth={1.75} />
      </button>
      <button className="toolbar-btn" onClick={() => canvas && redo(canvas)} style={btn} title={t('designer.redo')}>
        <Redo2 size={19} strokeWidth={1.75} />
      </button>

      <div style={sep} />

      {/* Alignment — fill color and opacity for the selected object already live in the
          right-hand PropertyPanel; duplicating them here just went out of sync with it
          (this panel's own re-render trigger never told that one a change had happened),
          making one or the other look broken depending on which a user touched first. */}
      <div style={{ display: 'flex', gap: 1, opacity: hasSelection ? 1 : 0.38, pointerEvents: hasSelection ? 'auto' : 'none' }}>
        {alignButtons.map(({ dir, icon, title }) => (
          <button key={dir} className="toolbar-btn" onClick={() => alignObject(dir)} style={btn} title={title}>{icon}</button>
        ))}
      </div>

      <div style={sep} />

      {/* Delete selected */}
      {hasSelection && (
        <button
          className="toolbar-btn"
          onClick={() => { canvas?.remove(selectedObject as any); canvas?.renderAll(); }}
          style={{ ...btn, color: '#ef4444' }}
          title={t('toolbar.delete')}
        >
          <Trash2 size={19} strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

const btn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '7px 8px',
  borderRadius: 6,
  color: 'rgba(0, 0, 0, 0.87)',
  lineHeight: 1,
};

const sep: React.CSSProperties = {
  width: 1,
  height: 28,
  background: '#e2e8f0',
  flexShrink: 0,
  margin: '0 2px',
};

// "Bounding box + object" convention — the same one Figma/Canva/Sketch use for align
// icons: an outline of the canvas, with a small solid block showing where the selected
// object lands relative to it. Matches what alignObject() above actually does
// (positioning relative to canvas width/height), so the icon reads as exactly what it does.
function AlignIcon({ objX, objY }: { objX: number; objY: number }) {
  return (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" style={{ display: 'block' }}>
      <rect x={1} y={1} width={14} height={14} rx={1.5} stroke="#94a3b8" strokeWidth={1.2} />
      <rect x={objX} y={objY} width={5} height={5} rx={1} fill="currentColor" />
    </svg>
  );
}

const alignIconLeft   = <AlignIcon objX={2}   objY={5.5} />;
const alignIconCenter = <AlignIcon objX={5.5} objY={5.5} />;
const alignIconRight  = <AlignIcon objX={9}   objY={5.5} />;
const alignIconTop    = <AlignIcon objX={5.5} objY={2}   />;
const alignIconBottom = <AlignIcon objX={5.5} objY={9}   />;
